import React from "react";
import axios from "axios";
import { CircleNotch, Check, UserFocus, FilmSlate, Users } from "@phosphor-icons/react";
import { fetchClonedLibrary, fetchAccountClones, mergeCloneVoices, pickDefaultVoice } from "@/lib/voice";

const FEATURED_FIELDS = [
  ["name_role", "Name / role or archetype", "e.g. Maya, a Black woman beauty founder"],
  ["industry", "Industry", "e.g. skincare / beauty"],
  ["appearance", "Appearance / representation", "Kept identical across every scene"],
  ["wardrobe", "Wardrobe", "What they wear"],
  ["environment", "Working environment", "Where the story scenes happen"],
  ["content_problem", "Content problem", "Their real problem showing up online"],
  ["consequence", "Consequence", "What it's costing them"],
  ["transformation", "Desired transformation", "What they want to change"],
];

/**
 * Story-led Hybrid controls: format toggle + HOST (real AI Twin + cloned
 * voice, the narrator) and the separate FEATURED CHARACTER the story is about.
 */
export const StoryHybridPanel = ({ API, authHeaders, storyConfig, onChange, onSave, saving }) => {
  const [twins, setTwins] = React.useState([]);
  // Two independent cloned-voice sources; merged for display.
  const [libVoices, setLibVoices] = React.useState([]);
  const [acctVoices, setAcctVoices] = React.useState([]);
  const [clonesLoading, setClonesLoading] = React.useState(true);
  const [voiceError, setVoiceError] = React.useState(null);
  const cfg = storyConfig || { story_format: "standard", host: {}, featured_character: {} };
  const host = cfg.host || {};
  const featured = cfg.featured_character || {};

  // Merged, de-duped cloned voices (ONLY valid clones — no stock voices).
  const cloneVoices = React.useMemo(
    () => mergeCloneVoices(acctVoices, libVoices).map((v) => ({
      id: v.id,
      label: (v.name || "Voice") + (v.is_default ? " · Default" : ""),
      is_default: v.is_default,
    })),
    [acctVoices, libVoices]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await axios.get(`${API}/ai-twin/images`, { headers: authHeaders });
        if (!cancelled) setTwins(t.data?.images || []);
      } catch (e) { /* no twins yet */ }
    })();

    // AUTHORITATIVE: same request as Clone My Voice, no timeout. Updates the
    // dropdown the moment it resolves — independent of the optional lookup.
    setClonesLoading(true);
    fetchClonedLibrary().then(({ voices, error }) => {
      if (cancelled) return;
      setLibVoices(voices);
      setVoiceError(error);            // error ONLY from the authoritative source
      setClonesLoading(false);
    });

    // OPTIONAL: supplemental live-account clones. Runs independently, may time
    // out, and NEVER produces an error or blocks the authoritative result.
    fetchAccountClones().then((voices) => {
      if (cancelled) return;
      if (voices.length) setAcctVoices(voices);
    });

    return () => { cancelled = true; };
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the user's actual default clone (e.g. bb7/31) by voice_id once
  // clones are available, keeping any voice they already chose.
  React.useEffect(() => {
    if (!cloneVoices.length) return;
    const pick = pickDefaultVoice(cloneVoices, host.voice_id);
    if (pick) onChange({ host: { voice_provider: "elevenlabs", voice_id: pick.id } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneVoices]);

  // Emit PATCHES only — the parent merges functionally against the LATEST
  // config so no update can ever clear a sibling key (story_format, host.*,
  // featured_character.*).
  const setFmt = (fmt) => onChange({ story_format: fmt });
  const setFeatured = (k, v) => onChange({ featured_character: { [k]: v } });

  const onVoiceChange = (val) => {
    const [provider, id] = val.split("::");
    onChange({ host: { voice_provider: provider, voice_id: id } });
  };
  const voiceValue = host.voice_provider && host.voice_id ? `${host.voice_provider}::${host.voice_id}` : "";

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 space-y-4" data-testid="story-hybrid-panel">
      {/* Format toggle */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <FilmSlate size={16} weight="fill" className="text-indigo-600" />
          <span className="text-sm font-bold text-zinc-900">Story Format</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setFmt("standard")}
            className={`text-xs px-3 py-2 rounded-lg border font-semibold ${cfg.story_format !== "story_hybrid" ? "bg-white border-indigo-500 text-indigo-700" : "bg-white/60 border-zinc-200 text-zinc-500"}`}
            data-testid="story-format-standard"
          >
            Standard Cinematic
          </button>
          <button
            onClick={() => setFmt("story_hybrid")}
            className={`text-xs px-3 py-2 rounded-lg border font-semibold ${cfg.story_format === "story_hybrid" ? "bg-white border-indigo-500 text-indigo-700" : "bg-white/60 border-zinc-200 text-zinc-500"}`}
            data-testid="story-format-hybrid"
          >
            Story-led Hybrid
          </button>
        </div>
      </div>

      {cfg.story_format === "story_hybrid" && (
        <>
          {/* HOST */}
          <div className="rounded-lg bg-white border border-zinc-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <UserFocus size={15} weight="fill" className="text-indigo-600" />
              <span className="text-xs font-bold text-zinc-900">Host / Narrator (your AI Twin)</span>
            </div>
            <p className="text-[11px] text-zinc-500">This is who tells the story in your voice. Separate from the featured character below.</p>
            <div className="grid grid-cols-4 gap-2" data-testid="host-twin-picker">
              {twins.length === 0 && <span className="text-[11px] text-amber-600 col-span-4">No AI Twins yet — add one in AI Twin Gallery.</span>}
              {twins.map((t, i) => {
                const nm = t.original_name || t.name || `Twin ${i + 1}`;
                const sel = host.twin_image_id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => onChange({ host: { twin_image_id: t.id, twin_image_filename: t.filename } })}
                    className={`relative flex flex-col items-center gap-0.5 p-1 rounded-lg border ${sel ? "border-indigo-500 bg-indigo-100/60" : "border-transparent hover:border-zinc-200"}`}
                    data-testid={`host-twin-${t.id}`}
                    title={nm}
                  >
                    {sel && <span className="absolute top-0.5 right-0.5 bg-indigo-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]">✓</span>}
                    <img src={`${API}/ai-twin/images/${t.filename}`} alt={nm} className={`w-full aspect-square object-cover rounded-md ${sel ? "ring-2 ring-indigo-400" : ""}`} />
                    <span className="text-[9px] text-zinc-600 truncate w-full text-center">{nm}</span>
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-[11px] font-semibold text-zinc-700 block mb-0.5">Narrator voice</label>
              <select value={voiceValue} onChange={(e) => onVoiceChange(e.target.value)} className="w-full text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white" data-testid="host-voice-select">
                <option value="">{clonesLoading ? "Loading your cloned voices…" : "Select a voice…"}</option>
                {cloneVoices.length > 0 && (
                  <optgroup label="Your cloned voices">
                    {cloneVoices.map((v) => <option key={`el-${v.id}`} value={`elevenlabs::${v.id}`}>{v.label}</option>)}
                  </optgroup>
                )}
              </select>
              <p className="text-[10px] mt-1" data-testid="host-voice-status">
                {clonesLoading
                  ? <span className="text-zinc-500">Loading your cloned voices…</span>
                  : cloneVoices.length > 0
                    ? <span className="text-emerald-600">✓ {cloneVoices.length} cloned voice{cloneVoices.length === 1 ? "" : "s"} available</span>
                    : <span className="text-amber-600">No cloned voices found. Create one in Clone My Voice — Story Hybrid needs your own cloned voice.</span>}
              </p>
              {voiceError && cloneVoices.length === 0 && (
                <p className="text-[10px] mt-1 text-rose-600" data-testid="host-voice-error">{voiceError}</p>
              )}
            </div>
          </div>

          {/* Quality + spend disclosure */}
          <div className="rounded-lg bg-white border border-indigo-200 p-3 text-[11px] text-zinc-600 space-y-0.5" data-testid="hybrid-quality-spend">
            <div className="flex items-center gap-1.5 font-semibold text-indigo-700">
              <FilmSlate size={13} weight="fill" /> Instagram HD · 1080×1920 (9:16) · recommended
            </div>
            <p><span className="font-semibold text-zinc-800">40 Lab credits</span> flat ($8.00 value) · est. provider spend ~$6.66 · blocked if it would exceed the $8.00 ceiling.</p>
            <p className="text-zinc-500">3 host shots + 5 story shots · 58–60s · H.264/AAC. If any shot fails you can Resume and only the missing shots re-render — no double charge.</p>
          </div>

          {/* FEATURED CHARACTER */}
          <div className="rounded-lg bg-white border border-zinc-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Users size={15} weight="fill" className="text-rose-500" />
              <span className="text-xs font-bold text-zinc-900">Featured Character (the story is about them)</span>
            </div>
            <p className="text-[11px] text-zinc-500">A different person from the host. Stays visually consistent across their story scenes.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FEATURED_FIELDS.map(([key, label, hint]) => (
                <div key={key}>
                  <label className="text-[11px] font-semibold text-zinc-700 block mb-0.5">{label}</label>
                  <textarea
                    value={featured[key] || ""}
                    onChange={(e) => setFeatured(key, e.target.value)}
                    placeholder={hint}
                    rows={2}
                    className="w-full text-[11px] border border-zinc-200 rounded-lg px-2 py-1.5 resize-none bg-white placeholder:text-zinc-400"
                    data-testid={`featured-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <button
        onClick={onSave}
        disabled={saving}
        className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
        data-testid="story-config-save-btn"
      >
        {saving ? <><CircleNotch size={13} className="animate-spin" /> Saving</> : <><Check size={13} weight="bold" /> Save Story Setup</>}
      </button>
    </div>
  );
};

export default StoryHybridPanel;
